import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { startSupportSession } from '../../api/endpoints';
import type { AdminUser } from '../../api/types';
import { useSession } from '../../app/session';

/** «Посмотреть как»: обязательная причина, затем режим только для чтения. */
export function ViewAsPanel({ user, onCancel }: { user: AdminUser; onCancel: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { switchSupport } = useSession();
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const start = useMutation({
    meta: { manualError: true },
    mutationFn: (r: string) => startSupportSession(user.id, r),
    onSuccess: async (s) => {
      await switchSupport(s.id);
      navigate('/', { replace: true });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.userMessage : t('errors.unknown')),
  });

  return (
    <section className="wiz" style={{ maxWidth: 'none', marginBottom: 22 }}>
      <h2>{t('admin.viewAs.title', { name: user.name })}</h2>
      <p className="muted small" style={{ margin: '-4px 0 14px', maxWidth: '70ch' }}>
        {t('admin.viewAs.intro')}
      </p>
      <label className="field">
        {t('admin.viewAs.reason')}
        <textarea
          className="input"
          autoFocus
          value={reason}
          placeholder={t('admin.viewAs.placeholder')}
          aria-invalid={!!err}
          onChange={(e) => {
            setReason(e.target.value);
            setErr(null);
          }}
        />
      </label>
      {err && (
        <div className="err" role="alert">
          {err}
        </div>
      )}
      <div className="wiz-foot">
        <button type="button" className="btn" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={start.isPending}
          onClick={() => {
            const r = reason.trim();
            if (r.length < 5) return setErr(t('admin.viewAs.required'));
            start.mutate(r);
          }}
        >
          {t('admin.viewAs.start')}
        </button>
      </div>
    </section>
  );
}
